import { useState, useEffect } from 'react'
import { Divider, FlexGrid, Spacer } from '@telus-uds/components-web'
import { Container, DetailsPadding } from './Styles'
import { renderSpeedInfo, renderButtonText, renderProductInfo } from './SectionRenderer'
import ProductDetails from './ProductDetails'
import { ImageProps } from '../../core/Image/Image'
import { IconProps } from '../../core/Icon/Icon'
import { LinkProps } from '../../core/Link/Link'
import { DetailsSectionProps } from './DetailsSection'

export type PlanSectionDetailFields = {
  headingIcon?: IconProps
  headingText?: string
  tooltipText?: string
  descriptionText?: string
  images?: ImageProps[]
  textLink?: LinkProps
}

export type PlanSectionProps = {
  productTitle?: string
  productIcon?: IconProps
  productSpeed?: string
  productSpeedRate?: string
  productPriceText?: string
  productBottomText?: string
  buttonText?: string
  detailsSection?: DetailsSectionProps[]
  expandButtonText?: string
  collapseButtonText?: string
  extraDetailsSection?: PlanSectionDetailFields[]
  adjustableWidth?: number
  expandButtonHex?: string
  buttonURL?: string
}

export type DefaultTypes = {
  COLORS: { [key: string]: string }
  IMAGE_LEFT_MARGIN: number
}

export const DEFAULTS: DefaultTypes = {
  COLORS: {
    SHUTTLE_GREY: '#676E73',
    TELUS_PURPLE: '#4B286D',
  },
  IMAGE_LEFT_MARGIN: 40,
}

const PlanSection = ({
  productTitle,
  productIcon,
  productSpeed,
  productSpeedRate,
  productPriceText,
  productBottomText,
  expandButtonText,
  collapseButtonText,
  buttonText,
  detailsSection,
  extraDetailsSection,
  adjustableWidth = 100,
  buttonURL,
}: PlanSectionProps) => {
  const [isMobile, setIsMobile] = useState(false)
  const MOBILE_SIZE = 768
  const { TELUS_PURPLE } = DEFAULTS.COLORS

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (window.document.body.clientWidth < MOBILE_SIZE) {
        setIsMobile(true)
      } else {
        setIsMobile(false)
      }
    })

    if (document) {
      resizeObserver.observe(document.body)
    }

    return () => resizeObserver.unobserve(document.body)
  }, [])

  return (
    <FlexGrid>
      <Container adjustableWidth={adjustableWidth} data-testid="plan-section-testid">
        <Spacer space={7} />
        <FlexGrid.Row>
          <FlexGrid.Col xs={12} md={4} horizontalAlign="center">
            <FlexGrid.Row>
              {renderProductInfo(productIcon, productTitle, productPriceText, TELUS_PURPLE)}
              <FlexGrid.Col xs={12}>
                <div>
                  {renderSpeedInfo(productSpeed, productSpeedRate, TELUS_PURPLE)}
                  {isMobile && (
                    <>
                      <Spacer space={4} />
                      <Divider variant={{ decorative: true }} />
                      <Spacer space={4} />
                    </>
                  )}
                </div>
              </FlexGrid.Col>
            </FlexGrid.Row>
          </FlexGrid.Col>
          <FlexGrid.Col xs={12} md={8}>
            <div style={{ display: 'flex' }}>
              {!isMobile && <Divider vertical variant={{ decorative: true }} />}
              <Spacer space={isMobile ? 2 : 7} direction="row" />
              <div style={{ display: 'block', minHeight: '200px', marginRight: '32px' }}>
                <DetailsPadding>
                  <ProductDetails
                    isMobile={isMobile}
                    collapseButtonText={collapseButtonText}
                    expandButtonText={expandButtonText}
                    detailsSection={detailsSection}
                    extraDetailsSection={extraDetailsSection}
                  />
                </DetailsPadding>
              </div>
            </div>
            <Spacer space={isMobile ? 2 : 7} />
          </FlexGrid.Col>
        </FlexGrid.Row>
        {buttonText && buttonURL && renderButtonText(buttonText, TELUS_PURPLE, isMobile, buttonURL, productBottomText)}
      </Container>
    </FlexGrid>
  )
}

export default PlanSection
